import { openTranscriptionSocket, startPcmCapture } from '#/shared/lib/stt'
import type { TranscriptResult, TranscriptionSocket } from '#/shared/lib/stt'

/**
 * One dictation recording session on ElevenLabs Scribe v2 Realtime: taps the
 * mic's PCM, streams it over the shared transcription socket, and accumulates
 * the committed utterances while the user speaks. Framework-free — the React
 * hook (`use-scribe-dictation`) owns UI state and calls `finalize`/`cancel`.
 */

/** RMS above this counts as speech (not room noise), used to detect silence. */
const VOICE_RMS = 0.01

/** Auto-stop dictation after this much (ms) silence, so an abandoned recording
 *  releases the mic instead of listening forever. */
const SILENCE_TIMEOUT_MS = 10_000

/** How often (ms) to poll the silence timer while listening. */
const SILENCE_POLL_MS = 500

/** How long (ms) after stop to wait for the server to commit the trailing
 *  utterance before falling back to its last partial hypothesis. */
const FINAL_COMMIT_GRACE_MS = 1_500

/** Seconds of injected silence sent on stop — the server commits an utterance
 *  when its VAD hears a pause, and once the mic stops there is no more audio to
 *  hear a pause IN, so we append one. */
const FLUSH_SILENCE_S = 1

/** Root-mean-square amplitude of a PCM chunk — our cheap voice-activity signal. */
function rms(chunk: Float32Array): number {
  let sum = 0
  for (const v of chunk) sum += v * v
  return Math.sqrt(sum / chunk.length)
}

export interface ScribeSessionOptions {
  apiKey: string
  /** Live microphone stream; the session taps it but the caller owns it. */
  mic: MediaStream
  /** Prolonged silence — the owner should run its normal stop flow. */
  onAutoStop: () => void
  /** Transport failure while recording (never fired after finalize/cancel). */
  onError: () => void
}

export interface ScribeSession {
  /** Stop capturing and resolve the final transcript ('' when nothing was said).
   *  Resolves quickly when everything is already committed; otherwise waits a
   *  short grace for the trailing utterance to commit. */
  finalize: () => Promise<string>
  /** Tear down without a transcript (unmount / error paths). */
  cancel: () => void
}

/** Accumulated transcript state, mutated by socket results. */
interface Transcript {
  committed: string[]
  /** Latest still-mutating hypothesis of the current utterance. */
  partial: string
  /** Whether speech was heard since the last commit — the tell that a trailing
   *  utterance is still unaccounted for at finalize time. */
  voicedSinceCommit: boolean
}

function applyResult(transcript: Transcript, result: TranscriptResult): void {
  if (result.isFinal) {
    transcript.committed.push(result.text)
    transcript.partial = ''
    transcript.voicedSinceCommit = false
  } else {
    transcript.partial = result.text
  }
}

/** Everything spoken this session: the committed utterances plus, when the
 *  trailing one never committed, its last partial hypothesis. */
function fullText(transcript: Transcript): string {
  return [...transcript.committed, transcript.partial]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Nothing is pending: the current utterance is committed and no speech has
 *  been heard since — the transcript is complete as-is. */
function isSettled(transcript: Transcript): boolean {
  return transcript.partial === '' && !transcript.voicedSinceCommit
}

export async function startScribeSession(
  options: ScribeSessionOptions,
): Promise<ScribeSession> {
  const transcript: Transcript = {
    committed: [],
    partial: '',
    voicedSinceCommit: false,
  }
  let ended = false
  /** A dead transport can't commit anything — finalize skips the grace wait. */
  let socketDown = false
  let lastVoiceAt = performance.now()
  let silenceTimer: ReturnType<typeof setTimeout> | null = null
  /** Set while finalize waits for the trailing commit; called on each result. */
  let onTranscriptSettled: (() => void) | null = null

  // Bound before the capture starts so its chunk callback can close over it;
  // assigned immediately (and synchronously) after, so no chunk is ever dropped
  // — and the socket itself queues audio while its token mints.
  let socket: TranscriptionSocket | null = null

  const capture = await startPcmCapture(options.mic, (chunk) => {
    if (ended) return
    socket?.sendAudio(chunk)
    if (rms(chunk) > VOICE_RMS) {
      lastVoiceAt = performance.now()
      transcript.voicedSinceCommit = true
    }
  })

  const openedSocket = openTranscriptionSocket({
    apiKey: options.apiKey,
    sampleRate: capture.sampleRate,
    onResult: (result) => {
      applyResult(transcript, result)
      if (isSettled(transcript)) onTranscriptSettled?.()
    },
    // The socket only reports status changes while we keep it open — after
    // finalize/cancel the handlers are silenced by `close`, so any error or
    // unexpected close seen here happened mid-recording.
    onStatus: (status) => {
      if (status === 'live') return
      socketDown = true
      if (!ended) options.onError()
    },
  })
  socket = openedSocket

  const watchSilence = (): void => {
    if (ended) return
    if (performance.now() - lastVoiceAt > SILENCE_TIMEOUT_MS) {
      options.onAutoStop()
      return
    }
    silenceTimer = setTimeout(watchSilence, SILENCE_POLL_MS)
  }
  watchSilence()

  /** Stop capturing; further chunks/status are ignored. Idempotent. */
  const end = (): void => {
    ended = true
    if (silenceTimer) clearTimeout(silenceTimer)
    capture.stop()
  }

  /** Wait (bounded) for the trailing utterance to commit after the mic stops. */
  const awaitTrailingCommit = (): Promise<void> =>
    new Promise((resolve) => {
      const timeout = setTimeout(resolve, FINAL_COMMIT_GRACE_MS)
      onTranscriptSettled = () => {
        clearTimeout(timeout)
        resolve()
      }
    })

  return {
    finalize: async () => {
      if (ended) return fullText(transcript)
      end()
      if (!isSettled(transcript) && !socketDown) {
        // Nudge the server's VAD to commit what it heard, then wait it out.
        openedSocket.sendAudio(
          new Float32Array(Math.round(capture.sampleRate * FLUSH_SILENCE_S)),
        )
        await awaitTrailingCommit()
      }
      openedSocket.close()
      return fullText(transcript)
    },
    cancel: () => {
      end()
      onTranscriptSettled?.()
      openedSocket.close()
    },
  }
}
